import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  auth,
  createUserWithEmailAndPassword,
} from "../utils/firebase";
import "./Signup.css";
import { doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  getAuth,
  PhoneAuthProvider,
  RecaptchaVerifier,
  multiFactor,
  sendEmailVerification,
  onAuthStateChanged,
} from "firebase/auth";
import { previewOffer, redeemOffer } from "../utils/offersClient";
import {
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import googleLogo from "./GoogleG.png";

function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const navigate = useNavigate();
  const defaultUserFields = {
    role: "user",
    jobBankRole: "Job Seeker",
    userType: "token",
    tokens: 2,
    desiredPurchase: 0,
    paying: false,
    emailVerified: false,
    mfaEnrolled: false,
    awaitingVerification: false,
    username: "",
    phone: "",
    createdAt: serverTimestamp(),
  };

  function initRecaptcha() {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        { size: "invisible" }
      );
    }
  }

  function normalizePhone(input) {
    if (!input) return "";
    // Remove spaces, dashes, parentheses
    let digits = input.replace(/[^\d+]/g, "");
    // If it starts with "1" and has 11 digits, prefix with +
    if (/^1\d{10}$/.test(digits)) {
      return "+" + digits;
    }
    // If it already starts with +, trust it
    if (digits.startsWith("+")) {
      return digits;
    }
    // If it’s just 10 digits, assume US/Canada and add +1
    if (/^\d{10}$/.test(digits)) {
      return "+1" + digits;
    }
    return digits; // fallback
  }

  async function tryRedeemSignupOffer() {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const idToken = await user.getIdToken();
    const baseUrl = process.env.REACT_APP_FLUENCY_SERVER;

    const url = new URL(window.location.href);
    const coupon = url.searchParams.get("coupon") || url.searchParams.get("code") || "";
    const utm = {
      source: url.searchParams.get("utm_source") || null,
      medium: url.searchParams.get("utm_medium") || null,
      campaign: url.searchParams.get("utm_campaign") || null,
    };

    const pv = await previewOffer(idToken, baseUrl, { coupon, utm });
    const selectedOfferId = pv?.offer?.id || null;
    await redeemOffer(idToken, baseUrl, {
      offerId: selectedOfferId,
      coupon,
      utm,
    });
  }

  const navigateTo = (path) => navigate(path);

  const isStrongPassword = (password) =>
    /^(?=.*[A-Z])(?=.*\d).{8,}$/.test(password);

  const handleSignup = async (event) => {
    event.preventDefault();
    if (window.gtag) {
      window.gtag("event", "sign_up", {
        method: "join_button",
      });
    }
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!isStrongPassword(password)) {
      setError("Password should be at least 8 characters, contain a number and an uppercase letter.");
      return;
    }
    if (!phone) {
      setError("Phone number is required for verification.");
      return;
    }
    if (!/^\+\d{10,15}$/.test(phone)) {
      throw new Error("Invalid phone format. Use e.g., +15555555555");
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const actionCodeSettings = {
        url: "https://ispeakwell.ca/login",   // where users land after verifying
        handleCodeInApp: false,
      };
      await sendEmailVerification(user, actionCodeSettings);

      await setDoc(doc(db, "users", user.uid), {
        ...defaultUserFields,
        email,
        firstName,
        lastName,
        phone,
        emailVerified: false,
        awaitingVerification: true,
      });

      setMessage("Signup successful. Please verify your email before continuing to phone verification.");
      setAwaitingVerification(true);
    } catch (err) {
      console.error("Error signing up:", err);
      setError(err.message);
    }
  };

  const handleGoogleSignup = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const [firstName, lastName = ""] = user.displayName
        ? user.displayName.split(" ")
        : ["", ""];

      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          ...defaultUserFields,
          email: user.email,
          firstName,
          lastName,
          emailVerified: user.emailVerified ?? false,
          phone: "",            // Google doesn’t give phone — leave blank
          awaitingVerification: true, 
        },
        { merge: true }
      );

      // Optionally trigger signup offer
      await tryRedeemSignupOffer();

      navigate("/voicebot");
    } catch (err) {
      console.error("Google sign-up error:", err);
      setError("Error signing up with Google. Please try again.");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.emailVerified && awaitingVerification) {
        try {
          await updateDoc(doc(db, "users", user.uid), { emailVerified: true, awaitingVerification: false });
          initRecaptcha();
          const provider = new PhoneAuthProvider(auth);
          const verificationId = await provider.verifyPhoneNumber(phone, window.recaptchaVerifier);
          setVerificationId(verificationId);
          setMessage("A verification code was sent to your phone. Please enter it below.");
        } catch (err) {
          setError("Error verifying phone number: " + err.message);
        }
      }
    });
    return () => unsubscribe();
  }, [awaitingVerification, phone]);

  const handleVerifyCode = async () => {
    if (!verificationId || !smsCode) {
      setError("Enter the code you received.");
      return;
    }
    try {
      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const mfaUser = multiFactor(auth.currentUser);
      await mfaUser.enroll(cred, "phone number");

      await setDoc(
        doc(db, "users", auth.currentUser.uid),
        { mfaEnrolled: true },
        { merge: true }
      );

      tryRedeemSignupOffer();
      navigate("/voicebot");
    } catch (err) {
      console.error("Error verifying code:", err);
      setError(err.message);
    }
  };

  return (
    <div className="SU-signup-container">
      <h1 className="SU-supheader-title">iSpeakWell</h1>
      <h5 className="SU-tagline">Become a member of the iSpeakWell crew.</h5>
      <p className="SU-subtitle">Real humans. Smart AI. Your fluency, faster.</p>

      <div className="SU-card">
        <h3 className="SU-card-title">Create your account</h3>
        <form className="SU-SignUp-Input" onSubmit={handleSignup}>
          <h4>Please enter your name:</h4>
          <input type="text" placeholder="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <input type="text" placeholder="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <h4>Please enter your email:</h4>
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          <input type={showPassword ? "text" : "password"} placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />

          <h4>Verify your phone number:</h4>
          <input type="tel" placeholder="+15555555555" value={phone} onChange={(e) => setPhone(normalizePhone(e.target.value))} />

          <button type="button" className="SUButtons" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? "Hide" : "Show"} Password
          </button>
          <button className="SUButtons" type="submit">Sign Up</button>
        </form>
        {/* Divider */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "1.2rem 0",
          color: "var(--isu-text-dim)",
          fontWeight: 600,
        }}>
          <span
            style={{
              width: "40%",
              height: "1px",
              background: "rgba(255,255,255,0.2)",
              marginRight: "10px",
            }}
          ></span>
          OR
          <span
            style={{
              width: "40%",
              height: "1px",
              background: "rgba(255,255,255,0.2)",
              marginLeft: "10px",
            }}
          ></span>
        </div>
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <button
            type="button"
            onClick={handleGoogleSignup}
            className="G-SUButtons"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
          >
            <img src={googleLogo} alt="Google logo" style={{ width: "20px", height: "20px" }} />
            Sign up with Google
          </button>
        </div>

        {/* MFA SMS step */}
        {verificationId && (
          <div className="SU-mfa-section">
            <h4>Enter SMS code:</h4>
            <input className="SU-input-sms" type="text" placeholder="123456" value={smsCode} onChange={(e) => setSmsCode(e.target.value)} />
            <button className="SUButtons" type="button" onClick={handleVerifyCode}>Verify & Continue</button>
          </div>
        )}

        {error && <p className="SU-error-message">{error}</p>}
        {message && <p className="SU-subtitle">{message}</p>}

        <h3 className="SU-link-text">
          Already have an account? <Link className="SU-LogLink" to="/login">Login</Link>
        </h3>
      </div>
      <div className="SU-links-section">
        <button className="PWRS-link-button" onClick={() => navigateTo("/about")}>About</button>
        <button className="SU-link-button" onClick={() => navigateTo("/careers")}>Careers</button>
        <button className="SU-link-button" onClick={() => navigateTo("/tokens")}>Tokens</button>
        <button className="SU-link-button" onClick={() => navigateTo("/contactus")}>Contact Us</button>
        <button className="SU-link-button" onClick={() => navigateTo("/login")}>Login</button>
      </div>

      <div id="recaptcha-container"></div>
    </div>
  );
}

export default Signup;
